import { Search, ShieldCheck, UserMinus, UserPlus } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import type { Vendor, VendorsListResponse } from '@/types'
import { useVendorAuth } from '@/auth/VendorAuthProvider'
import { Spinner } from '@/components/ui'
import { RequestError, api } from '@/lib/api'
import { AdminSection } from './AdminSection'

function fmtDate(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-AR')
}

export function AdminAdmins() {
  const { vendor: me } = useVendorAuth()
  const [vendors, setVendors] = useState<Vendor[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)

  async function load() {
    setErr(null)
    try {
      setVendors((await api.get<VendorsListResponse>('/vendors')).vendors)
    } catch (e) {
      setErr((e as Error).message)
    }
  }
  useEffect(() => {
    void load()
  }, [])

  const admins = useMemo(() => (vendors ?? []).filter((v) => v.isAdmin && v.active), [vendors])
  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (vendors ?? [])
      .filter((v) => !v.isAdmin && v.active)
      .filter((v) => !q || v.fullName.toLowerCase().includes(q) || v.email.toLowerCase().includes(q))
      .slice(0, 8)
  }, [vendors, search])

  // Promover / revocar reusa PATCH /vendors/{id}. El backend valida SELF_DEMOTE y
  // LAST_ADMIN; optimistic update por el read-after-write lag de MD.
  async function setAdmin(v: Vendor, isAdmin: boolean) {
    setErr(null)
    setBusyId(v.id)
    const snapshot = vendors
    setVendors((list) => list?.map((x) => (x.id === v.id ? { ...x, isAdmin } : x)) ?? null)
    try {
      await api.patch(`/vendors/${v.id}`, { isAdmin })
    } catch (e) {
      setVendors(snapshot ?? null)
      setErr(e instanceof RequestError ? e.message : 'No se pudo actualizar.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <AdminSection title="Administradores">
      {err && <div className="mb-3 rounded-lg bg-danger/5 px-3 py-2 text-sm text-danger">{err}</div>}

      {vendors === null ? (
        <div className="flex justify-center py-10"><Spinner className="h-6 w-6 accent-text" /></div>
      ) : (
        <div className="space-y-5">
          <section>
            <h3 className="mb-2 font-semibold text-ink">Administradores actuales</h3>
            <div className="space-y-2">
              {admins.map((v) => (
                <div key={v.id} className="card flex items-center gap-3 p-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full accent-bg-soft accent-text">
                    <ShieldCheck className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-ink">
                      {v.fullName}
                      {me?.id === v.id && <span className="ml-1 text-xs accent-text">(vos)</span>}
                    </p>
                    <p className="truncate text-xs text-ink-muted">{v.email}</p>
                    <p className="text-[10px] text-ink-faint">Desde {fmtDate(v.createdAt)}</p>
                  </div>
                  <button
                    className="btn-secondary px-3 py-1.5 text-xs disabled:opacity-40"
                    disabled={me?.id === v.id || busyId === v.id}
                    onClick={() => setAdmin(v, false)}
                    title={me?.id === v.id ? 'No podés quitarte tu propio acceso' : 'Quitar acceso'}
                  >
                    {busyId === v.id ? <Spinner className="h-3.5 w-3.5" /> : <><UserMinus className="h-3.5 w-3.5" /> Quitar</>}
                  </button>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3 className="mb-1 font-semibold text-ink">Agregar administrador</h3>
            <p className="mb-2 text-xs text-ink-muted">Solo se puede promover a vendedores existentes.</p>
            <div className="mb-2 flex items-center gap-2 rounded-xl border border-line bg-surface-sunken px-3">
              <Search className="h-4 w-4 text-ink-faint" />
              <input
                className="w-full bg-transparent py-2.5 text-sm outline-none placeholder:text-ink-faint"
                placeholder="Buscar vendedor…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {candidates.length === 0 ? (
              <p className="text-xs text-ink-faint">{search ? 'Sin coincidencias.' : 'No hay vendedores para promover.'}</p>
            ) : (
              <div className="space-y-2">
                {candidates.map((v) => (
                  <div key={v.id} className="card flex items-center gap-3 p-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-ink">{v.fullName}</p>
                      <p className="truncate text-xs text-ink-muted">{v.email} · Nº {v.vendorNumber}</p>
                    </div>
                    <button className="btn-primary px-3 py-1.5 text-xs" disabled={busyId === v.id} onClick={() => setAdmin(v, true)}>
                      {busyId === v.id ? <Spinner className="h-3.5 w-3.5" /> : <><UserPlus className="h-3.5 w-3.5" /> Promover</>}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </AdminSection>
  )
}
